import React, { useEffect, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

/**
 * CardPagination - A reusable infinite scroll wrapper component
 * 
 * Props:
 * - hasMore (boolean): Whether there are more pages/records to load
 * - loading (boolean): Whether a fetch operation is currently in progress
 * - onLoadMore (function): Callback triggered when user scrolls to the bottom
 * - children (node): The list of cards or other elements to display
 * - sentinelMargin (string): IntersectionObserver margin (default "100px")
 * - loaderComponent (node): Custom loader component to show when loading is true
 */
const CardPagination = ({
    hasMore,
    loading,
    onLoadMore,
    children,
    sentinelMargin = "100px",
    loaderComponent,
}) => {
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (!hasMore || loading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    onLoadMore();
                }
            },
            {
                root: null, // relative to document viewport
                rootMargin: sentinelMargin, // load content before reaching the absolute bottom
                threshold: 0.1,
            }
        );

        const currentSentinel = sentinelRef.current;
        if (currentSentinel) {
            observer.observe(currentSentinel);
        }

        return () => {
            if (currentSentinel) {
                observer.unobserve(currentSentinel);
            }
        };
    }, [hasMore, loading, onLoadMore, sentinelMargin]);

    return (
        <Box sx={{ width: "100%" }}>
            {children}
            
            {/* Scroll Sentinel */}
            {hasMore && (
                <Box 
                    ref={sentinelRef} 
                    sx={{ 
                        height: "10px", 
                        width: "100%", 
                        visibility: "hidden" 
                    }} 
                />
            )}

            {/* Loading Spinner / Indicator */}
            {loading && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 4,
                        width: "100%",
                    }}
                >
                    {loaderComponent || (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <CircularProgress size={20} thickness={4} />
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                Loading more...
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* End of results message */}
            {!hasMore && !loading && (
                <Box sx={{ textAlign: "center", py: 4, width: "100%" }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing all records
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default CardPagination;
